// Funciones puras para las calculadoras del entrenador. Todo en kg.

// ── 1RM (máximo de una repetición) ───────────────────────────────────
export function epley1RM(weight: number, reps: number): number {
  if (reps <= 1) return weight;
  return weight * (1 + reps / 30);
}

export function brzycki1RM(weight: number, reps: number): number {
  if (reps <= 1) return weight;
  if (reps >= 37) return weight; // fórmula se rompe en reps altas
  return (weight * 36) / (37 - reps);
}

/** Estimación de 1RM promediando Epley y Brzycki. */
export function estimate1RM(weight: number, reps: number): number {
  if (weight <= 0 || reps <= 0) return 0;
  if (reps === 1) return weight;
  return (epley1RM(weight, reps) + brzycki1RM(weight, reps)) / 2;
}

/** Reps aproximadas alcanzables a un % del 1RM (inversa de Epley). */
export function repsAtPercent(percent: number): number {
  if (percent >= 100) return 1;
  return Math.max(1, Math.round(30 * (100 / percent - 1)));
}

export interface PercentRow {
  percent: number;
  weight: number;
  reps: number;
}

/** Tabla de cargas a distintos % del 1RM. */
export function percentTable(oneRM: number): PercentRow[] {
  const percents = [100, 95, 90, 85, 80, 75, 70, 65, 60, 55, 50, 40];
  return percents.map((percent) => ({
    percent,
    weight: (oneRM * percent) / 100,
    reps: repsAtPercent(percent),
  }));
}

// ── RPE / RIR → %1RM ─────────────────────────────────────────────────
export interface RpeResult {
  oneRM: number;
  percent: number; // % del 1RM que representa la serie
  repsToFailure: number;
}

/**
 * Estima 1RM y %1RM de una serie a un RPE dado (Epley con reps en reserva).
 * RIR = 10 - RPE; reps a fallo = reps + RIR.
 */
export function rpeEstimate(weight: number, reps: number, rpe: number): RpeResult {
  if (weight <= 0 || reps <= 0) return { oneRM: 0, percent: 0, repsToFailure: 0 };
  const rir = Math.max(0, 10 - rpe);
  const repsToFailure = reps + rir;
  const oneRM = epley1RM(weight, repsToFailure);
  const percent = oneRM > 0 ? (weight / oneRM) * 100 : 0;
  return { oneRM, percent, repsToFailure };
}

// ── Wilks (fuerza relativa) ──────────────────────────────────────────
const WILKS_MALE = [-216.0475144, 16.2606339, -0.002388645, -0.00113732, 7.01863e-6, -1.291e-8];
const WILKS_FEMALE = [594.31747775582, -27.23842536447, 0.82112226871, -0.00930733913, 4.731582e-5, -9.054e-8];

/** Puntaje Wilks: total levantado ajustado por peso corporal y sexo. */
export function wilksScore(total: number, bodyweight: number, sex: 'male' | 'female'): number {
  if (total <= 0 || bodyweight <= 0) return 0;
  const c = sex === 'female' ? WILKS_FEMALE : WILKS_MALE;
  const bw = bodyweight;
  const denom =
    c[0] + c[1] * bw + c[2] * bw ** 2 + c[3] * bw ** 3 + c[4] * bw ** 4 + c[5] * bw ** 5;
  if (denom === 0) return 0;
  return (500 / denom) * total;
}

// ── IMC (BMI) ────────────────────────────────────────────────────────
export interface BmiResult {
  bmi: number;
  category: string;
}

export function bmi(weightKg: number, heightCm: number): BmiResult {
  if (weightKg <= 0 || heightCm <= 0) return { bmi: 0, category: '' };
  const m = heightCm / 100;
  const value = weightKg / (m * m);
  let category = 'Normal';
  if (value < 18.5) category = 'Bajo peso';
  else if (value < 25) category = 'Normal';
  else if (value < 30) category = 'Sobrepeso';
  else category = 'Obesidad';
  return { bmi: value, category };
}

/** Redondea a 1 decimal para mostrar (sin ceros de más). */
export function round1(n: number): number {
  return Math.round(n * 10) / 10;
}
